import { createSlice} from "@reduxjs/toolkit";
import {
    chargeDebitFilterConfig,
    chargesDebitFilters,
    checkLevelFilterConfig,
    checkLevelFilters,
    claimlevelFilterConfig,
    claimlevelFilters,
    creditBalanceFilter,
    creditBalanceFilterConfig,
    denialFilterConfig,
    denialFilters,
    openArFilterConfig,
    openArFilters,
    paymentAdjustmentsFilterConfig,
    paymentAdjustmentsFilters,
    preBillingProductivityFilterConfig,
    preBillingProductivityFilters,
    priorAuthFilterConfig,
    priorAuthFilters,
    transactionFilterConfig,
    transactionFilters
} from "../filterConstants";
import { PAGING_END_INDEX } from "../../../../utilities/staticConfigs";
import { fetchReportData, generateReportData } from "./asyncThunkAPI";

let initialState = {
    reportType: 1,
    reportTypeName: "Transaction",
    isGenerateMode: false,
    selectedError:"",
    showErrorModal: false,
    showModalWindow: false,
    filterConfig: transactionFilterConfig,
    activeFilters: transactionFilters,
    tableFilters:{
        report_type:"",
        report_status:"",
        from_date:"",
        to_date:"",
        search_key:""
    },
    reportData: [],
    loading: false,
    notifyMessage:"",
    showNotification: false,
    statusTag:"",
    openAppliedFilterModal:false,
    pageSize: 0,
    activePage: 1,
    count: 0,
    totalPage: 0,
    startIndex: 0,
    endIndex: PAGING_END_INDEX,
}

export const reportGeneratorSlice = createSlice({
    name: "reportGenerator",
    initialState,
    reducers: {

        /**
         * Updates pagination indices when navigating to the next page
         * @param {*} state 
         * @param {*} actions 
         */
        onPageNext: (state, actions) => {
            state.startIndex = actions.payload.startIndex;
            state.endIndex = actions.payload.endIndex
        },

        /**
         * Updates pagination indices when navigating to the previous page
         * @param {*} state 
         * @param {*} actions 
         */
        onPagePrevious: (state, actions) => {
            state.startIndex = actions.payload.startIndex;
            state.endIndex = actions.payload.endIndex
        },

        /**
         * Sets the current active page number
         * @param {*} state 
         * @param {*} actions 
         */

        setActivePage: (state, actions) => {
            state.activePage = actions.payload.active
        },
        
        /**
         * Toggles the visibility of the generate report modal
         * @param {*} state 
         * @param {*} action 
         */
        handleGeneratClick: (state, action) => {
            state.showModalWindow = action.payload
        },

        /**
         * Resets the entire report generator state to its initial values
         */
        onResetData: () => initialState,

        /**
         * function to show errors after report generation.
         * @param {*} state 
         * @param {*} action 
         */
        reportTableErrorPopup: (state, action) => {
            state.selectedError = action.payload.selectedError;
            state.showErrorModal = action.payload.showErrorModal;
        },

        /**
         * function to close error modal.
         * @param {*} state 
         */
        handleErrorModalClose: (state) => {
            state.showErrorModal = false;
        },

        /**
         * function to handle report selection.
         * @param {*} state 
         * @param {*} action 
         */
        handleReportSelection: (state, action) => {
            const { name, value } = action.payload;
            state.reportType = value;
            state.reportTypeName = name;

            /**
             * switch case to select report filter and report filter config
             * The report type will be get from the select box
             */
            switch (name) {
                case "Charges/Debit":
                    state.activeFilters = chargesDebitFilters;
                    state.filterConfig = chargeDebitFilterConfig;
                    break;
                case "Open AR":
                    state.activeFilters = openArFilters;
                    state.filterConfig = openArFilterConfig
                    break;
                case "Payment/Adjustment":
                    state.activeFilters = paymentAdjustmentsFilters;
                    state.filterConfig = paymentAdjustmentsFilterConfig
                    break;
                case "Transaction":
                    state.activeFilters = transactionFilters;
                    state.filterConfig = transactionFilterConfig;
                    break;
                case "Check Level":
                    state.activeFilters = checkLevelFilters;
                    state.filterConfig = checkLevelFilterConfig;
                    break;
                case "Denial":
                    state.activeFilters = denialFilters;
                    state.filterConfig = denialFilterConfig;
                    break;
                case "Claim Level":
                    state.activeFilters = claimlevelFilters;
                    state.filterConfig = claimlevelFilterConfig;
                    break;
                case "Prior Authorization/Referral":
                    state.activeFilters = priorAuthFilters
                    state.filterConfig = priorAuthFilterConfig;
                    break;
                case "Insurance Credit Balance Report":
                    state.activeFilters = creditBalanceFilter
                    state.filterConfig = creditBalanceFilterConfig;
                    break;
                case "Pre-Billing Productivity Report":
                    state.activeFilters = preBillingProductivityFilters
                    state.filterConfig = preBillingProductivityFilterConfig;
                    break;
                default:
                    state.activeFilters = {};
                    state.filterConfig = []
            }
        },

        /**
         * modal window to show the filter
         * @param {*} state 
         * @param {*} action 
         */
        setShowModalWindow: (state, action) => {
            state.showModalWindow = action.payload;
        },

        /**
         * modal window to show the applied filter
         * @param {*} state 
         * @param {*} action 
         */
        setShowFilterModalWindow:(state,action)=>{
            state.openAppliedFilterModal = action.payload;
            if(action.payload==false){
                state.activeFilters={};
            }
        },

        /**
         * function to show the filter value
         * @param {*} state 
         * @param {*} actions 
         * @returns 
         */
        updateFieldValues: (state, actions) => {
            const updateState = actions.payload.state;
            let value = actions.payload.value;
            const field = actions.payload.field;
            
            if (updateState !== 'mainState') {
                return {
                    ...state,
                    [updateState]: {
                        ...state[updateState],
                        [field]: value,
                    }
                }
            } else {
                return {
                    ...state,
                    [field]: value,
                }
            }
        },

        /**
         * function to store table filter value.
         */
        updateTableFilterValue : (state,actions)=>{
            let {value,field}=actions.payload;
            state.tableFilters[field]=value;
        },

        /**
         * function to reset table filters.
         * @param {*} state 
         */
        onTableFilterReset:(state)=>{
            state.tableFilters=initialState.tableFilters
            state.activePage = 1
            state.startIndex = 0; 
            state.endIndex = PAGING_END_INDEX;
        },

        /**
         * function to show applied filters that used generate report.
         * @param {*} state 
         * @param {*} actions 
         */
        showAppliedFiltersPopup:(state,actions)=>{
            let {filters,report_type}=actions.payload;
            state.openAppliedFilterModal=true;
            state.activeFilters=filters;
            state.reportTypeName=report_type;
            /**
             * switch case to select report filter and report filter config
             * The report type will be get from the select box
             */
            switch (report_type) {
                case "Charge Debit Report":
                    state.filterConfig = chargeDebitFilterConfig;
                    break;
                case "Open AR Report":
                    state.filterConfig = openArFilterConfig
                    break;
                case "Payment Adjustment Report":
                    state.filterConfig = paymentAdjustmentsFilterConfig
                    break;
                case "Transaction Report":
                    state.filterConfig = transactionFilterConfig;
                    break;
                case "Check Level Report":
                    state.filterConfig = checkLevelFilterConfig;
                    break;
                case "Denial Report":
                    state.filterConfig = denialFilterConfig;
                    break;
                case "Claim Level Report":
                    state.filterConfig = claimlevelFilterConfig;
                    break;
                case "Prior Authorization Referral Report":
                    state.filterConfig = priorAuthFilterConfig;
                    break;
                case "Insurance Credit Balance Report":
                    state.filterConfig = creditBalanceFilterConfig;
                    break;
                case "Pre Billing Productivity Report":
                    state.filterConfig = preBillingProductivityFilterConfig;
                    break;
                default:
                    state.filterConfig = []
            }
        },

        /**
         * functuon to reset filter value that entered for generating.
         * @param {*} state 
         * @param {*} actions 
         */
        onResetReportFilter: (state, actions) => {
            const name  = actions.payload;
            switch (name) {
                case "Charges/Debit":
                    state.activeFilters = chargesDebitFilters;
                    break;
                case "Open AR":
                    state.activeFilters = openArFilters;
                    break;
                case "Payment/Adjustment":
                    state.activeFilters = paymentAdjustmentsFilters;
                    break;
                case "Transaction":
                    state.activeFilters = transactionFilters;
                    break;
                case "Check Level":
                    state.activeFilters = checkLevelFilters;
                    break;
                case "Denial":
                    state.activeFilters = denialFilters;
                    break;
                case "Claim Level":
                    state.activeFilters = claimlevelFilters;
                    break;
                case "Prior Authorization/Referral":
                    state.activeFilters = priorAuthFilters;
                    break;
                case "Insurance Credit Balance Report":
                    state.activeFilters = creditBalanceFilter;
                    break;
                default:
                    state.activeFilters = {};
            } 
        },

        /**
         * function to create notification.
         * @param {*} state 
         * @param {*} action 
         */
        showNotifications: (state, action) => {
            const { message, statusTag } = action.payload;
            state.showNotification = true;
            state.notifyMessage = message;
            state.statusTag = statusTag;
        },

        /**
         * function to clear notification.
         * @param {*} state 
         * @param {*} action 
         */
        clearNotification: (state) => {
            state.showNotification = false;
            state.notifyMessage = '';
            state.statusTag = '';
        },
    },

    extraReducers: (builder) => {
        
        builder.addCase(fetchReportData.pending,(state) => {
            state.loading = true;
        })
        builder.addCase(fetchReportData.fulfilled, (state, action) => {
            const { data,count,page_size, message } = action.payload || {};
            if(Array.isArray(data)){
                state.reportData = data
                state.totalPage = Math.ceil(count / page_size); 
                state.loading = false;
            } else {
                state.notifyMessage = message || "An error occurred while fetching Report!";
                state.showNotification = true;
                state.statusTag = "error"
            }
        })
        builder.addCase(fetchReportData.rejected, (state, action) => {
            const {message } = action.payload || {};
            state.reportData = [];
            state.loading = false;
            state.notifyMessage = message || "An error occurred while fetching Report!";
            state.showNotification = true;
            state.statusTag = "error" 
        })
        builder.addCase(generateReportData.pending, (state) => {
            state.loading = true;
        })
        builder.addCase(generateReportData.fulfilled, (state, action)=> {
            const {message, statusCode} = action.payload;
            
            state.loading = false
            state.showNotification = true
            state.notifyMessage = message
            state.statusTag = statusCode
        })
        builder.addCase(generateReportData.rejected, (state)=> {
            state.loading = false;
        })
    }

})

export const {
    updateFieldValues,
    handleGeneratClick,
    setShowModalWindow,
    updateFilters,
    handleReportSelection,
    updateTableFilterValue,
    reportTableErrorPopup,
    onResetReportFilter,
    onTableFilterReset,
    handleErrorModalClose,
    setShowFilterModalWindow,
    showAppliedFiltersPopup,
    showNotifications,
    clearNotification,
    setActivePage,
    onPagePrevious,
    onPageNext
} = reportGeneratorSlice.actions

export default reportGeneratorSlice.reducer;